import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";
import { usePartnerCode } from "./usePartnerCode";
import { getSignupAttributionMetadata } from "@/lib/attribution";
import { trackMetaEvent } from "@/services/analytics";

type SignUpInput = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  referralCode?: string;
  captchaToken?: string;
  partnerCode?: string; // Explicitly pass partner code
  heardAboutSource?: string; // "How did you hear about us?" answer
}

export const useAuthForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { redeemCode } = usePartnerCode();

  const onSuccess = () => {
    navigate("/onboarding");
  };

  const handleSignUp = async (input: SignUpInput) => {
    const { email, password, firstName, lastName, phone, referralCode, captchaToken, partnerCode, heardAboutSource } = input;
    const normalizedCaptchaToken = captchaToken || undefined;

    // get rererrer if available
    let referrerId: string | null = null;
    if (referralCode) {
      const { data, error} = await supabase.from("profiles")
        .select("id")
        .eq("referral_code", referralCode)
        .maybeSingle();

      if (error || !data?.id) {
        console.error("Error getting referrer:", error);
        throw new Error("Invalid referral code");
      }

      referrerId = data.id;
      
      // Clear the referral code from URL after signup to prevent double-discount
      // (user already receives signup credits, shouldn't also get checkout discount)
      const url = new URL(window.location.href);
      url.searchParams.delete('referralCode');
      window.history.replaceState({}, '', url.toString());
    }

    // Build first-touch attribution metadata (UTMs, click IDs, _fbp/_fbc, referrer,
    // landing path) plus the heard-about-source answer. The handle_new_user trigger
    // reads these from raw_user_meta_data and writes them to the profiles table.
    const attributionMetadata = getSignupAttributionMetadata(heardAboutSource);

    // Generate a stable per-signup event ID for Pixel ↔ CAPI deduplication.
    // Persisted to profiles.signup_event_id via the handle_new_user trigger,
    // and fired with the Pixel CompleteRegistration event below as { eventID }.
    const signupEventId = crypto.randomUUID();
    attributionMetadata.signup_event_id = signupEventId;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        captchaToken: normalizedCaptchaToken,
        data: {
          first_name: firstName,
          last_name: lastName,
          phone,
          referred_by: referrerId, // set referrer_by if available
          ...attributionMetadata,
        },
      },
    });
      
    if (error) throw error;
    if (data?.user) {
      // Fire Meta Pixel CompleteRegistration with the same event_id that was
      // persisted to profiles.signup_event_id. PR3b's CAPI relay will dedupe
      // server-side events against this ID.
      // Note: fbq accepts a flat params object; `custom_data` is a CAPI server-side
      // concept, so we send heard_about_source as a top-level custom param here.
      trackMetaEvent(
        "CompleteRegistration",
        {
          content_name: "nimi_signup",
          status: "completed",
          value: 0,
          currency: "GBP",
          heard_about_source: heardAboutSource ?? "unknown",
        },
        signupEventId,
      );

      // Redeem partner code if present
      if (partnerCode) {
        const redemptionTimestamp = new Date().toISOString();
        console.log(`🎁 [${redemptionTimestamp}] Attempting partner code redemption:`, {
          userId: data.user.id,
          partnerCode: partnerCode,
          email: email,
        });
        
        try {
          await redeemCode(data.user.id, partnerCode);
          console.log(`✅ [${redemptionTimestamp}] Partner code redeemed successfully for user ${data.user.id}`);
        } catch (err) {
          const errorTimestamp = new Date().toISOString();
          console.error(`❌ [${errorTimestamp}] Failed to redeem partner code:`, {
            userId: data.user.id,
            partnerCode: partnerCode,
            error: err,
          });
          
          // Show warning toast to user
          toast({
            title: "Partner code issue",
            description: "Your account was created but there was an issue applying the partner offer. You can activate it from your profile.",
            variant: "default",
          });
          // Don't block signup if code redemption fails
        }
      } else {
        console.log('ℹ️ No partner code provided during signup');
      }

      toast({
        title: "Success",
        description: "Account created successfully!",
      });

      onSuccess();
    }
  };

  const handleSignIn = async (email: string, password: string, captchaToken?: string) => {
    const normalizedCaptchaToken = captchaToken || undefined;
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        captchaToken: normalizedCaptchaToken,
      },
    });

    if (error) throw error;

    if (data?.user) {
      toast({
        title: "Success",
        description: "Signed in successfully!",
      });
      onSuccess();
    }
  };

  const handleResetPassword = async (email: string, captchaToken?: string) => {
    // First, create a password reset event record

    const { error: resetEventError } = await supabase
      .from('password_reset_events')
      .insert({
        email: email,
        requested_at: new Date().toISOString(),
      });

    if (resetEventError) {
      console.error("Error logging password reset event:", resetEventError);
      // Continue with password reset even if logging fails
    }

    // Send password reset email
    // log user in and allow them to update password on the profile page
    const redirectTo = `${window.location.origin}/profile?action=update-password`; 
    const normalizedCaptchaToken = captchaToken || undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { 
      redirectTo,
      captchaToken: normalizedCaptchaToken,
    });

    if (error) throw error;
    toast({
      title: "Success",
      description: "If an account exists with this email, you will receive a password reset link.",
    });
  };

  const handleConfirmPassword = async (newPassword: string) => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    
    const { data: { user }, error: createSessionError } = await supabase.auth.exchangeCodeForSession(code);
    if (createSessionError) throw createSessionError;

    // Update the password
    const { error: updatePasswordError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updatePasswordError) throw updatePasswordError;

    // Update the password reset event record
    if (user) {
      const { error: updateEventError } = await supabase
        .from('password_reset_events')
        .update({ 
          completed_at: new Date().toISOString(),
        })
        .eq('email', user.email)
        .is('completed_at', null)
        .order('requested_at', { ascending: false })
        .limit(1);

      if (updateEventError) {
        console.error("Error updating password reset event:", updateEventError);
        // Continue even if logging fails
      }
    }

    toast({
      title: "Success",
      description: "Password reset successfully!",
    });
  };

  return {
    handleSignUp,
    handleSignIn,
    handleResetPassword,
    handleConfirmPassword,
  };
};
